import { useEffect, useRef, useState } from 'react';

export type NetworkMode = 'lan' | 'wan' | 'checking' | 'unknown';

export interface UseLANReachabilityResult {
  mode: NetworkMode;
  probeUrl: string;
  consecutiveSuccesses: number;
  consecutiveFailures: number;
}

export const REACHABILITY_HYSTERESIS = 3;
export const REACHABILITY_INTERVAL_MS = 5_000;
export const REACHABILITY_TIMEOUT_MS = 3_000;

interface ReachabilityOverrides {
  hysteresis?: number;
  intervalMs?: number;
  timeoutMs?: number;
}

function readOverrides(): ReachabilityOverrides {
  if (typeof window === 'undefined') return {};
  const raw = (window as unknown as { __RSC_DASH_REACHABILITY?: ReachabilityOverrides })
    .__RSC_DASH_REACHABILITY;
  return raw ?? {};
}

function buildProbeUrl(primaryUrl: string): string {
  // Probe die Base-URL selbst (statt <base>/api/): HA beantwortet / mit 200,
  // /api/ ohne Token dagegen mit 401 — und jeder 401 erzeugt im Browser einen
  // Console-Error "Failed to load resource" pro Probe-Intervall (RSC-237
  // Follow-up: Console-Noise trotz erfolgreichem Connect). Die Root-URL
  // beweist Erreichbarkeit genauso, ohne Auth und ohne Console-Noise.
  try {
    const u = new URL(primaryUrl);
    u.search = '';
    u.hash = '';
    if (!u.pathname.endsWith('/')) u.pathname += '/';
    return u.toString();
  } catch {
    return primaryUrl;
  }
}

async function probeOnce(url: string, timeoutMs: number): Promise<boolean> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    // no-cors so CORS-blocked responses are treated as opaque success rather
    // than as network failures. Timeouts + real network errors still throw.
    const res = await fetch(url, {
      method: 'HEAD',
      mode: 'no-cors',
      cache: 'no-store',
      signal: controller.signal,
      credentials: 'omit',
    });
    if (res.type === 'opaque') return true;
    // JEDE HTTP-Antwort beweist Erreichbarkeit — auch 401/403/404. HA
    // beantwortet /api/ ohne Token mit 401; das als "nicht erreichbar" zu
    // werten loeste im LAN faelschlich den WAN-Fallback aus (RSC-237:
    // wss://<wan>/api/websocket → 302-Redirect → Reconnect-Loop). Seit dem
    // Follow-up proben wir die Base-URL (200), das Verhalten bleibt als
    // Absicherung fuer beliebige konfigurierte URLs bestehen.
    return true;
  } catch {
    return false;
  } finally {
    clearTimeout(timer);
  }
}

export function useLANReachability(primaryUrl: string): UseLANReachabilityResult {
  const probeUrl = buildProbeUrl(primaryUrl);
  const [mode, setMode] = useState<NetworkMode>('unknown');
  const [consecutiveSuccesses, setConsecutiveSuccesses] = useState(0);
  const [consecutiveFailures, setConsecutiveFailures] = useState(0);

  const successRef = useRef(0);
  const failureRef = useRef(0);
  const modeRef = useRef<NetworkMode>('unknown');
  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;
    successRef.current = 0;
    failureRef.current = 0;
    modeRef.current = 'unknown';
    setMode('unknown');
    setConsecutiveSuccesses(0);
    setConsecutiveFailures(0);

    const overrides = readOverrides();
    const hysteresis = overrides.hysteresis ?? REACHABILITY_HYSTERESIS;
    const intervalMs = overrides.intervalMs ?? REACHABILITY_INTERVAL_MS;
    const timeoutMs = overrides.timeoutMs ?? REACHABILITY_TIMEOUT_MS;

    const applyResult = (ok: boolean) => {
      if (cancelledRef.current) return;

      if (ok) {
        successRef.current += 1;
        failureRef.current = 0;
      } else {
        failureRef.current += 1;
        successRef.current = 0;
      }
      setConsecutiveSuccesses(successRef.current);
      setConsecutiveFailures(failureRef.current);

      if (ok && successRef.current >= hysteresis && modeRef.current !== 'lan') {
        modeRef.current = 'lan';
        setMode('lan');
      } else if (
        !ok &&
        failureRef.current >= hysteresis &&
        modeRef.current !== 'wan'
      ) {
        modeRef.current = 'wan';
        setMode('wan');
      } else if (modeRef.current === 'unknown') {
        modeRef.current = 'checking';
        setMode('checking');
      }
    };

    const runProbe = async () => {
      if (cancelledRef.current) return;
      // navigator.onLine=false is a strong hint we're off the LAN. Treat as
      // failure without paying the network timeout.
      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        applyResult(false);
        return;
      }
      const ok = await probeOnce(probeUrl, timeoutMs);
      applyResult(ok);
    };

    void runProbe();
    const interval = window.setInterval(runProbe, intervalMs);

    const onOnline = () => void runProbe();
    const onOffline = () => applyResult(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);

    return () => {
      cancelledRef.current = true;
      window.clearInterval(interval);
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, [probeUrl]);

  return { mode, probeUrl, consecutiveSuccesses, consecutiveFailures };
}
